import { SelectableTag } from 'components/Form/SelectableTag/SelectableTag';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useSelector } from 'react-redux';
import {
    filterAddonsByBusinessAreas,
    getActiveAddonServices
} from 'reducers/addonServices/AddonServicesSelectors';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { dispatch } from 'store';
import {
    navigateToStep,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import {
    getOnlineBookingBoardingAvailability,
    getOnlineBookingBoardingAvailabilityLoading,
    getOnlineBookingBoardingItems
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { DaycareAvailabilityDropOffSlot, DaycareAvailabilityPickUpSlot } from 'types/Availability';
import { OnlineBookingStep, OnlineBookingTime } from 'types/OnlineBooking';
import { assignBoardingUnitsForPets } from 'utils/boardingUtils';
import { convertMomentToOnlineBookingTime } from 'utils/daycareUtils';
import classes from './OnlineBookingBoardingTimePicker.module.scss';

const isSameTime = (a?: OnlineBookingTime, b?: OnlineBookingTime) => {
    if (!a || !b) return false;
    return a.hour === b.hour && a.minute === b.minute;
};

export const OnlineBookingBoardingTimePicker: React.FC = () => {
    const marketplace = useMarketplace();

    const addonServices = filterAddonsByBusinessAreas(useSelector(getActiveAddonServices), [
        MarketplaceServiceCategory.BOARDING
    ]);
    const bookingItems = useSelector(getOnlineBookingBoardingItems);
    const boardingAvailability = useSelector(getOnlineBookingBoardingAvailability);
    const boardingAvailabilityLoading = useSelector(getOnlineBookingBoardingAvailabilityLoading);

    const selectedCheckInTime = bookingItems[0]?.checkInTime;
    const selectedCheckOutTime = bookingItems[0]?.checkOutTime;

    // "24 hours from drop-off" services don't let the customer pick a check-out:
    // pick-up is the same time of day on the end date (drop-off + N×24h).
    const isDropoff24h = bookingItems[0]?.service?.boardingMetadata?.dropoff24h ?? false;

    const checkInSlots = React.useMemo(() => {
        return boardingAvailability?.dropOffSlots ?? [];
    }, [boardingAvailability?.dropOffSlots]);

    const selectedCheckInSlot = React.useMemo(() => {
        return checkInSlots.find(slot =>
            isSameTime(selectedCheckInTime, convertMomentToOnlineBookingTime(slot.datetime))
        );
    }, [checkInSlots, selectedCheckInTime]);

    const checkOutSlots = React.useMemo(() => {
        return selectedCheckInSlot?.pickUpSlots ?? [];
    }, [selectedCheckInSlot?.pickUpSlots]);

    const isValid = React.useMemo(() => {
        return (
            selectedCheckInTime !== undefined &&
            selectedCheckOutTime !== undefined &&
            bookingItems.every(item => item.roomUnitId !== undefined)
        );
    }, [bookingItems, selectedCheckInTime, selectedCheckOutTime]);

    const backHandler = () => {
        dispatch(navigateToStep(OnlineBookingStep.DatePicker));
    };

    const checkInChangeHandler = (slot: DaycareAvailabilityDropOffSlot) => {
        const checkInTime = convertMomentToOnlineBookingTime(slot.datetime);

        // For "24 hours from drop-off" services the check-out isn't chosen by the
        // customer: auto-select the pick-up slot at the same time of day so we can
        // keep the check-out grid hidden and the booking still gets a valid time.
        if (isDropoff24h) {
            const matchingPickUpSlot = slot.pickUpSlots.find(pickUpSlot =>
                isSameTime(checkInTime, convertMomentToOnlineBookingTime(pickUpSlot.datetime))
            );

            const roomUnitIds = matchingPickUpSlot
                ? assignBoardingUnitsForPets(
                      matchingPickUpSlot.rooms,
                      bookingItems.map(item => item.pet)
                  )
                : null;

            if (matchingPickUpSlot && roomUnitIds) {
                const checkOutTime = convertMomentToOnlineBookingTime(matchingPickUpSlot.datetime);

                dispatch(
                    updateOnlineBookingBookingItems(
                        bookingItems.map((item, index) => ({
                            ...item,
                            checkInTime,
                            checkOutTime,
                            roomUnitId: roomUnitIds[index]
                        }))
                    )
                );

                return;
            }

            // No 24h-aligned pick-up available: reflect the check-in but leave the
            // check-out unset so the footer stays disabled.
        }

        const updatedItems = bookingItems.map(item => ({
            ...item,
            checkInTime,
            checkOutTime: undefined,
            roomUnitId: undefined
        }));

        dispatch(updateOnlineBookingBookingItems(updatedItems));
    };

    const checkOutChangeHandler = (slot: DaycareAvailabilityPickUpSlot) => {
        const roomUnitIds = assignBoardingUnitsForPets(
            slot.rooms,
            bookingItems.map(item => item.pet)
        );

        if (!roomUnitIds) {
            return;
        }

        const checkOutTime = convertMomentToOnlineBookingTime(slot.datetime);

        const updatedItems = bookingItems.map((item, index) => ({
            ...item,
            checkOutTime,
            roomUnitId: roomUnitIds[index]
        }));

        dispatch(updateOnlineBookingBookingItems(updatedItems));
    };

    const submitHandler = () => {
        if (addonServices.length > 0) {
            return dispatch(navigateToStep(OnlineBookingStep.AddonList));
        }

        dispatch(navigateToStep(OnlineBookingStep.Instructions));
    };

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn>
                <h1 className="h2 underlined w-full">Select hours</h1>

                <div className={classes.timeGroup}>
                    <p className={classes.timeGroupLabel}>Check-in</p>

                    <div className={classes.timeGrid}>
                        {checkInSlots.map(slot => {
                            const checkInTime = convertMomentToOnlineBookingTime(slot.datetime);

                            return (
                                <SelectableTag
                                    key={slot.datetime.toISOString()}
                                    className={classes.timeTag}
                                    selected={isSameTime(selectedCheckInTime, checkInTime)}
                                    onClick={() => checkInChangeHandler(slot)}
                                >
                                    {checkInTime.label}
                                </SelectableTag>
                            );
                        })}
                    </div>
                </div>

                {selectedCheckInSlot && isDropoff24h && selectedCheckOutTime && (
                    <div className={classes.timeGroup}>
                        <p className={classes.timeGroupLabel}>Check-out</p>

                        <p className={classes.fixedTime}>
                            {selectedCheckOutTime.label} · same time as check-in
                        </p>
                    </div>
                )}

                {selectedCheckInSlot && !isDropoff24h && (
                    <div className={classes.timeGroup}>
                        <p className={classes.timeGroupLabel}>Check-out</p>

                        <div className={classes.timeGrid}>
                            {checkOutSlots.map(slot => {
                                const checkOutTime = convertMomentToOnlineBookingTime(
                                    slot.datetime
                                );

                                return (
                                    <SelectableTag
                                        key={slot.datetime.toISOString()}
                                        className={classes.timeTag}
                                        selected={isSameTime(selectedCheckOutTime, checkOutTime)}
                                        onClick={() => checkOutChangeHandler(slot)}
                                    >
                                        {checkOutTime.label}
                                    </SelectableTag>
                                );
                            })}
                        </div>
                    </div>
                )}
            </LeftColumn>
            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        label="Continue"
                        loading={boardingAvailabilityLoading}
                        disabled={!isValid}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
